import { useEffect, useRef, useState } from "react";
import { stringifyUrl } from "query-string";
import * as Sentry from "@sentry/react";

import { IGeoAddress } from "common/types";
import { googlePlaceToAddress } from "common/utils/geo";
import { loadExternalScript } from "common/load/utils";
import { useClinicGroupCountry } from "common/contexts/UIContext";
import { CountryCode, US_DOMESTIC_COUNTRIES } from "common/constants/address";

export type AddressAutosuggestConfig = { onAddressSelect: (value: IGeoAddress) => void };

/**
 * Hook to enable autosuggesting an address on input
 *
 * @param config configuration object that contains options and callbacks
 * @returns a ref to pass to the input that will control the suggestion
 */
export const useAddressAutosuggest = (config: AddressAutosuggestConfig) => {
  const { onAddressSelect } = config;

  const clinicGroupCountry = useClinicGroupCountry();
  const [isMapsAPILoaded, setIsMapsAPILoaded] = useState(false);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Lazily load the external Google maps <script />.
   *
   * No cleanup needed to avoid needing to redownload the script on other pages
   */
  useEffect(() => {
    const loadAutoComplete = async () => {
      await loadExternalScript(
        stringifyUrl({
          url: "https://maps.googleapis.com/maps/api/js",
          query: {
            key: window.GOOGLE_MAPS_BROWSER_KEY,
            libraries: "places",
          },
        }),
      );

      setIsMapsAPILoaded(true);
    };

    loadAutoComplete();
  }, []);

  /**
   * Loads the autocomplete module for use with an input once the maps API is loaded
   *
   * Removes listeners on the input when unmounting the hook
   */
  useEffect(() => {
    const input = inputRef.current;

    if (!isMapsAPILoaded || !input) {
      return undefined;
    }

    // It appears there are some cases that we load the autosuggest API but `google` is
    // still not set. They are pretty rare. Let's track these as warnings for now so that
    // we can understand how often this happens and for what devices.
    if (!("google" in window) || !google || !google.maps) {
      Sentry.captureMessage(
        `Google api was loaded but isn't available. ${
          "google" in window ? "Maps api not set." : "Main google variable not set."
        }`,
        "warning",
      );
      return undefined;
    }

    setAutocomplete(
      // Also implicitly creates a `div.pac-container` to render autocomplete ui.
      // Unfortunately, it is entirely controlled by the vendor with no way to remove
      // the div related to this. Cleaning up event listeners is the closest we can get
      // for now // but it may be worth looking at going down to places.AutocompleteService
      // if it becomes an issue in the future
      new google.maps.places.Autocomplete(input, {
        // see https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-multiple-countries
        // See https://www.notion.so/PIMS-Supported-Countries-2bd15182b69b802b8d0ce56fde24db27
        componentRestrictions: {
          country:
            // US and all US territories map to "US"
            clinicGroupCountry.toUpperCase() in US_DOMESTIC_COUNTRIES
              ? [
                  CountryCode.US,
                  CountryCode.AS,
                  CountryCode.GU,
                  CountryCode.PR,
                  CountryCode.VI,
                  // Unfortunately, this field supports only 5 countries at a time, so we
                  // have omitted CountryCode.MP from the list (based on lowest population)
                ]
              : clinicGroupCountry.toUpperCase(), // other countries map to the country code
        },
        fields: ["address_components"],
        strictBounds: false,
        types: ["address"],
      }),
    );

    // When hitting enter on an address option, we don't want that to bubble up and submit a form,
    // since it usually just indicates to fill in the form with the address selected
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    };
    input.addEventListener("keydown", handleKeydown);

    return () => {
      google.maps.event.clearInstanceListeners(input);
      input.removeEventListener("keydown", handleKeydown);
    };
  }, [isMapsAPILoaded, clinicGroupCountry]);

  /**
   * Sets the listeners for when a place is selected
   *
   * Removes outdated listeners when new ones are passed in
   */
  useEffect(() => {
    if (!autocomplete) {
      return () => null;
    }

    // Listen for selecting the address in the dropdown
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const address = googlePlaceToAddress(place);
      onAddressSelect(address);
    });

    return () => listener.remove();
  }, [autocomplete, onAddressSelect]);

  return inputRef;
};
